import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { fetchBlogPosts, blogHeroUrl } from "@/lib/api";

function formatDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return "";
  }
}

/**
 * "Field notes" — surfaces the newest three blog posts on the landing
 * page so first-time visitors have crawlable, indexable content above
 * the fold-below and returning visitors see fresh drops without needing
 * to click into /blog.
 */
export default function RecentBlogStrip() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetchBlogPosts({ limit: 3 });
        if (!cancelled) setPosts(res.posts || []);
      } catch {
        if (!cancelled) setPosts([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  if (loading || posts.length === 0) return null;

  return (
    <section
      className="mt-20 sm:mt-28 border-t border-obsidian-600 pt-12 sm:pt-16"
      aria-labelledby="recent-notes-heading"
      data-testid="landing-recent-blog"
    >
      <div className="flex items-end justify-between gap-6 mb-8 sm:mb-10 flex-wrap">
        <div>
          <p className="font-sans uppercase tracking-[0.28em] text-xs text-terracotta mb-2">
            Field notes
          </p>
          <h2
            id="recent-notes-heading"
            className="font-serif text-3xl sm:text-4xl text-bone-100 leading-tight"
          >
            The Journal — recent essays
          </h2>
        </div>
        <Link
          to="/blog"
          data-testid="landing-blog-all-link"
          className="font-sans text-xs uppercase tracking-[0.24em] text-bone-400 hover:text-terracotta focus:outline-none focus-visible:underline inline-flex items-center gap-2 min-h-[44px] px-3 py-2 sm:min-h-0 sm:px-1 sm:py-1 border-b border-transparent hover:border-terracotta"
        >
          Read all essays <ArrowRight className="h-3 w-3" aria-hidden="true" />
        </Link>
      </div>

      <ul className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8 list-none p-0">
        {posts.map((p) => (
          <li key={p.id} className="list-none">
            <article
              className="group h-full flex flex-col border border-obsidian-600 bg-obsidian-800/30 hover:border-terracotta/70 focus-within:border-terracotta transition-colors"
              data-testid={`landing-blog-card-${p.slug}`}
            >
              <Link to={`/blog/${p.slug}`} className="block focus:outline-none">
                <div className="relative aspect-[16/9] overflow-hidden bg-obsidian-800">
                  {p.hero_url ? (
                    <img
                      src={blogHeroUrl(p.id)}
                      alt={p.hero_alt || p.title}
                      loading="lazy"
                      width="800"
                      height="450"
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                    />
                  ) : (
                    <div
                      className="w-full h-full flex items-center justify-center text-bone-600 font-serif italic"
                      aria-hidden="true"
                    >
                      Umbra
                    </div>
                  )}
                </div>
              </Link>
              <div className="p-5 sm:p-6 flex flex-col flex-1">
                <div className="flex items-center gap-2 mb-3 flex-wrap">
                  {p.category && (
                    <span className="font-sans text-[0.7rem] uppercase tracking-[0.22em] text-terracotta">
                      {p.category}
                    </span>
                  )}
                  <span className="font-sans text-[0.7rem] uppercase tracking-[0.22em] text-bone-500">
                    · {formatDate(p.published_at)}
                  </span>
                  {p.reading_time && (
                    <span className="font-sans text-[0.7rem] uppercase tracking-[0.22em] text-bone-500">
                      · {p.reading_time} min
                    </span>
                  )}
                </div>
                <h3 className="font-serif text-xl text-bone-100 leading-snug mb-2">
                  <Link
                    to={`/blog/${p.slug}`}
                    className="inline-block py-1 hover:text-terracotta focus:outline-none focus-visible:text-terracotta focus-visible:underline transition-colors"
                  >
                    {p.title}
                  </Link>
                </h3>
                <p className="font-serif italic text-bone-400 text-sm leading-relaxed line-clamp-3 mb-4">
                  {p.excerpt || p.meta_description}
                </p>
                <Link
                  to={`/blog/${p.slug}`}
                  data-testid={`landing-blog-read-${p.slug}`}
                  aria-label={`Read ${p.title}`}
                  className="mt-auto inline-flex items-center gap-2 min-h-[44px] px-2 -mx-2 py-2 sm:min-h-0 sm:px-0 sm:mx-0 sm:py-1 font-sans text-[0.65rem] uppercase tracking-[0.24em] text-bone-300 hover:text-terracotta focus:outline-none focus-visible:text-terracotta focus-visible:underline"
                >
                  Read <ArrowRight className="h-3 w-3" aria-hidden="true" />
                </Link>
              </div>
            </article>
          </li>
        ))}
      </ul>
    </section>
  );
}
